import { useState } from 'react';
import Alert from '@v2/shared/components/Alert';
import Button from '@v2/shared/components/Button';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

type Props = {
  /** The address that has not been verified. */
  email: string;
  /** Where Resend posts — site-relative, so it goes through buildUrl. */
  resendUrl: string;
  className?: string;
};

/**
 * "Your email address is not yet verified", with a way to ask for the mail again.
 *
 * The V2 counterpart of DMagzWeb's EmailVerificationBanner, which the legacy account
 * layout drew above every page under it. A page that moves to the V2 account layout
 * leaves that layout behind and has to carry this itself — which matters most on
 * notification preferences, since changing the address there is what makes it
 * unverified in the first place.
 *
 * The endpoint answers the word SENT and nothing else, so success is the status code.
 */
export default function EmailVerificationBanner({ email, resendUrl, className }: Props) {
  const { buildUrl } = useUrlGenerator();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const resend = () => {
    setStatus('sending');

    fetch(buildUrl(resendUrl), { method: 'POST', credentials: 'same-origin' })
      .then((response) => setStatus(response.ok ? 'sent' : 'error'))
      .catch(() => setStatus('error'));
  };

  if (status === 'sent') {
    return (
      <Alert color="success" className={className}>
        A verification email was sent to <strong>{email}</strong>. Follow the link to verify your
        email address.
        <br />
        If you did not receive it, please check your <strong>spam</strong> box.
      </Alert>
    );
  }

  return (
    <>
      <Alert color="warning" className={className}>
        <div className="md:flex md:items-center gap-4">
          <div>
            Your email address (<strong>{email}</strong>) is not yet verified.
            <br />
            Please check your email and follow the link to verify your email address.
          </div>

          <Button
            type="button"
            color="primary"
            size="sm"
            className="mt-3 md:mt-0 md:ml-auto shrink-0"
            onClick={resend}
            isLoading={status === 'sending'}
            loadingText="Sending"
          >
            Resend Email
          </Button>
        </div>
      </Alert>

      {status === 'error' && (
        <Alert color="danger">
          There was an error in sending your verification email. Please try again, or{' '}
          <a href={buildUrl('/contact')}>contact us</a> if the error persists.
        </Alert>
      )}
    </>
  );
}
